/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import PaginatedListState, { type Page, type PaginatedListParams, type PaginatedListRequestParams } from 'flarum/common/states/PaginatedListState';
import ItemList from 'flarum/common/utils/ItemList';
import setRouteWithForcedRefresh from 'flarum/common/utils/setRouteWithForcedRefresh';
import type { ApiPayloadPlural, ApiPayloadSingle, ApiResponsePlural, ApiResponseSingle } from 'flarum/common/Store';
import type Article from 'flarum/orell-articles/models/Article';
import type Bookshelf from '../../common/models/Bookshelf';

const pageSize = 12;

export interface BookListFilterableParams {
	filter?: PaginatedListRequestParams['filter'];
	sort?: string;
}

export default class BookListState extends PaginatedListState<Article> {
	extraArticles: Article[] = [];

	bookshelf!: ApiResponseSingle<Bookshelf>;

	// Re-declared public so the state can be instantiated with `new` (TS2674).
	public constructor(params?: PaginatedListParams, page?: number, size?: number) {
		super(params, page, size);
	}

	get type(): string {
		return 'bookshelf_articles';
	}

	initFirstPage(bookshelf: ApiResponseSingle<Bookshelf>): void {
		this.pageSize = pageSize;
		this.bookshelf = bookshelf;

		const results = (bookshelf.articles() as Article[]).slice(0, pageSize) as ApiResponsePlural<Article>;
		// A preloaded single-resource document still carries pagination links.
		const payload = bookshelf.payload as ApiPayloadSingle & Pick<ApiPayloadPlural, 'links'>;
		results.payload = { links: { ...payload.links } } as ApiPayloadPlural;

		this.parseResults(1, results);
	}

	protected requestParams(): PaginatedListRequestParams {
		const params = { ...super.requestParams() }; // Clone for immutability

		params.sort = this.sortMap()[m.route.param('sort')];

		this.addStatusFilter(params);

		return params;
	}

	getParams(): PaginatedListParams {
		const params = { ...super.getParams() }; // Clone for immutability
		if (!params.sort) {
			params.sort = m.route.param('sort');
		}
		return params;
	}

	addStatusFilter(params: BookListFilterableParams): void {
		let statusId: string | undefined;
		try {
			statusId = m.route.param('filter')['status'];
		} catch (error) {}

		if (statusId) {
			params.filter = {
				...params.filter,
				status: statusId,
			};
		}
	}

	/** URL sort key (also the translation key) to API sort value. */
	sortMap(): Record<string, string> {
		const map: Record<string, string> = {};

		Object.keys(this.sortMapItems().toObject()).forEach((key) => {
			const item = this.sortMapItems().get(key);
			map[key] = item;
		});

		return map;
	}

	sortMapItems(): ItemList<string> {
		const items = new ItemList<string>();

		items.add('latest', '-bookshelfArticle.updatedAt', 100);
		items.add('author_az', 'author', 81);
		items.add('author_za', '-author', 80);
		items.add('title_az', 'title', 71);
		items.add('title_za', '-title', 70);
		items.add('category_az', 'category', 61);
		items.add('category_za', '-category', 60);
		items.add('newest', '-publishedAt', 51);
		items.add('oldest', 'publishedAt', 50);
		items.add('leastPages', 'pages', 41);
		items.add('mostPages', '-pages', 40);

		return items;
	}

	changeSort(sort: string): void {
		const params = this.params;
		delete params.filter.bookshelf;
		delete params.page;

		this.addStatusFilter(params);

		if (sort === Object.keys(this.sortMap())[0]) {
			delete params.sort;
		} else {
			params.sort = sort;
		}

		setRouteWithForcedRefresh(app.route.book_collection(this.bookshelf, params));
	}

	addArticles(articles: Article | Article[] | null | undefined): void {
		if (!articles) {
			return;
		}
		const newArticles = Array.isArray(articles) ? articles : [articles];

		this.extraArticles = newArticles.concat(this.extraArticles);

		m.redraw();
	}

	public getAllItems(): Article[] {
		return this.extraArticles.concat(super.getAllItems());
	}

	getPages(): Page<Article>[] {
		const pages = super.getPages();

		if (this.extraArticles.length) {
			return [
				{
					number: -1,
					items: this.extraArticles,
				},
				...pages,
			];
		}

		return pages;
	}
}
